import React, { Component } from 'react';

import { DataTable } from 'components/dataTable';
import { Grid } from 'components/grid';
import { Select } from 'components/input';
import { ORDER_BY } from 'components/table/constants';
import { I18n, translate } from 'components/translate';
import DateHelper from 'helpers/Date';
import { stripPTag } from 'helpers/String';
import { UserManagerService } from 'modules/userManager';
import { HISTORY_TYPES, HISTORY_KEYS } from 'modules/userManager/constants';
import UserDetailService from 'modules/userManager/userDetail/UserDetailService';
import PropTypes from 'prop-types';

import './UserDetailHistory.scss';

const FILTER_OPTIONS = Object.entries(HISTORY_TYPES);

const DEFAULT_SIZE_VALUE = 10;

@translate()
export default class UserDetailHistory extends Component {
  static propTypes = {
    t: PropTypes.func,
    userData: PropTypes.object
  };

  state = {
    filter: '',
    items: [],
    loading: true,
    page: 0,
    size: 10,
    sort: { value: ORDER_BY.DESC }
  };

  columns = [
    {
      name: 'userDetailDate',
      label: 'general.date',
      defaultOrderBy: ORDER_BY.DESC,
      sortName: 'userDetailDate'
    },
    { name: 'userDetailActivity', label: 'general.activity' }
  ];

  componentDidMount() {
    const { sort, size } = this.state;

    this.fetchItems({ sort: this.sortParams(sort), size });
  }

  formatDate(date) {
    return date && DateHelper.format(date, 'DD/MM/YYYY');
  }

  setLoading = (loading = false) => this.setState({ loading });

  fetchItems(params = {}) {
    this.setLoading(true);

    const { id: userId } = this.props.userData;

    return UserManagerService.userHistorics(userId, params)
      .then(({ content: items, page = 0, totalElements }) => {
        this.setState({ items, page, totalElements });
        this.setLoading(false);
      })
      .catch(() => {
        this.setLoading(false);
      });
  }

  getHistoricDescription({ activity, metaData = {} }) {
    if (!activity) {
      return '';
    }

    const { t } = this.props;
    const isQuiz = activity === HISTORY_KEYS.QUIZ_COMPLETION;
    const type = isQuiz
      ? HISTORY_TYPES[activity][metaData.quizType]
      : HISTORY_TYPES[activity];

    if (type) {
      const { DESCRIPTION } = type;

      const data = { ...metaData };

      for (const field in data) {
        if (field) {
          data[field] = stripPTag(data[field]);
        }
      }

      return t(DESCRIPTION, { data: { ...data } });
    }

    return '';
  }

  get filter() {
    const { filter } = this.state;
    const hasFilter = filter && filter.value;

    return hasFilter ? filter.value : null;
  }

  get getEmptyMessage() {
    const filter = this.filter;

    return filter ? HISTORY_TYPES[filter].EMPTY_MSG : 'general.no_activity';
  }

  sortParams = (sort) => `eventDate,${Object.values(sort)[0]}`;

  updateItems = () => {
    const { page = 0, size = DEFAULT_SIZE_VALUE, filter, sort } = this.state;
    const params = {
      page,
      size,
      ...(sort && { sort: this.sortParams(sort) }),
      ...(filter && { activity: filter.value })
    };

    this.fetchItems(params);
  };

  changePerPage = (size) => this.setState({ size, page: 0 }, this.updateItems);

  changePage = (page) => this.setState({ page }, this.updateItems);

  changeSort = (sort) => this.setState({ sort }, this.updateItems);

  onFilterChange = (filter = '') =>
    this.setState({ filter, page: 0 }, this.updateItems);

  get items() {
    const { items, size = DEFAULT_SIZE_VALUE } = this.state;

    return items.slice(0, size);
  }

  renderFilter = () => {
    const { filter, items } = this.state;
    const shouldRenderFilter = (items && items.length) || filter;
    const options = FILTER_OPTIONS.map(UserDetailService.getHistoricType);

    if (shouldRenderFilter) {
      return (
        <Select
          name="class"
          className="user__detail__history__header__select"
          value={filter}
          placeholder="users_manager.users_detail.activity_filter"
          options={options}
          onChange={this.onFilterChange}
        />
      );
    }

    return null;
  };

  renderTableRow = (item) => {
    const [{ label: dateLabel }, { label: activityLabel }] = this.columns;

    return (
      <DataTable.Row>
        <DataTable.DataColumns>
          <DataTable.Cell columnName={dateLabel}>
            <div>{this.formatDate(item.eventDate)}</div>
          </DataTable.Cell>

          <DataTable.Cell columnName={activityLabel}>
            <div>{this.getHistoricDescription(item)}</div>
          </DataTable.Cell>
        </DataTable.DataColumns>
      </DataTable.Row>
    );
  };

  renderHistoryTable = () => {
    const { items, loading, page, totalElements, size } = this.state;

    return (
      <DataTable
        columns={this.columns}
        items={items}
        loading={loading}
        onChangePerPage={this.changePerPage}
        onChangePage={this.changePage}
        onChangeSort={this.changeSort}
        pagination={{ page, totalElements }}
        perPage={size}
      >
        <DataTable.Header></DataTable.Header>

        <DataTable.Body>
          {!loading && this.items.map(this.renderTableRow)}
        </DataTable.Body>

        <DataTable.Footer />
      </DataTable>
    );
  };

  render() {
    const { items, loading } = this.state;
    const shouldRenderTable = (items && items.length) || loading;

    return (
      <Grid className="user__detail__history" block>
        <div className="user__detail__history__header">
          <h2>
            <I18n label="users_manager.users_detail.historic" />
          </h2>
          {this.renderFilter()}
        </div>

        {shouldRenderTable ? (
          this.renderHistoryTable()
        ) : (
          <div className="empty_content">
            <I18n label={this.getEmptyMessage} />
          </div>
        )}
      </Grid>
    );
  }
}
